import {PAdbContext} from "../PA.Core/PAdbContext";
import {ILease} from "../PA.Utils/PA.UtilsIF";
import * as AsyncLock from 'async-lock'
import * as _ from 'lodash';
import {GenericLease} from "../PA.Utils/GenericLease";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {WB_ApprovalRow} from "../PA.Core/PAdbSchemaRows";
import {esWB_Approval} from "../PA.Core/PAdbSchema";
import {IApprovalServiceOptions} from "../PA.TPM.Services/PA.TPM.Services.IF";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {IwbApproval} from "../PA.Core/PAdbSchemaRowsIF";

export class ApprovalService {

    private static _dbContext: PAdbContext;
    private static _lease: ILease;
    private static _bIsLoaded: boolean = false;
    private static dctLock: any = new AsyncLock();

    private static dctApprovalByWbID: { [wbApprovalID: number]: WB_ApprovalRow } = {};
    private static dctApprovals: { [wbBusinessUnitID: number]: { [approvalTypeID: string]: { [approvalSubsetID: string]: WB_ApprovalRow[] } } } = {};
    private static dctApprovalsByOptions: { [wbBusinessUnitID: number]: { [approvalTypeID: string]: { [approvalSubsetID: string]: {[optionsString: string]: WB_ApprovalRow} } } } = {};


    static getApprovalRule(wbBusinessUnitID: number, approvalTypeID: string, approvalSubtypeID: string, options?: IApprovalServiceOptions): Promise<WB_ApprovalRow> {

        return new Promise<WB_ApprovalRow>((resolve, error) => {

            ApprovalService.dctLock.acquire('global',
                (done: any) => {
                    ApprovalService.checkLease()
                        .then((ignore) => {
                            let retVal = ApprovalService.findApprovalFromCache(wbBusinessUnitID, approvalTypeID, approvalSubtypeID, options);
                            done(null, null);
                            resolve(retVal);
                        })
                        .catch((err) => {
                            done(err);
                            error(err);
                        })
                })

        })
    }

    //<editor-fold desc="Private Methods">

    private static findApprovalFromCache(wbBusinessUnitID: number, approvalTypeID: string, approvalSubtypeID: string, options?: IApprovalServiceOptions): WB_ApprovalRow {

        let optionsDctKey: string = ApprovalService.getOptionsDctKey(options);

        //first, check to see if this combination of keys and options has been cached already
        if (wbBusinessUnitID in ApprovalService.dctApprovalsByOptions) {
            let dctByApprovalType = ApprovalService.dctApprovalsByOptions[wbBusinessUnitID];
            if (approvalTypeID in dctByApprovalType) {
                let dctBySubtypeID = dctByApprovalType[approvalTypeID];
                if (approvalSubtypeID in dctBySubtypeID) {
                    let ruleDct = dctBySubtypeID[approvalSubtypeID];
                    if (optionsDctKey in ruleDct) {
                        return ruleDct[optionsDctKey];
                    }
                }
            }
        }

        //if it hasn't been cached, then get all the rules for the required field, find the valid one given the options, then cache it
        let approvalRows: WB_ApprovalRow[];
        if (wbBusinessUnitID in ApprovalService.dctApprovals) {
            let dctByApprovalType = ApprovalService.dctApprovals[wbBusinessUnitID];
            if (approvalTypeID in dctByApprovalType) {
                let dctBySubtypeID = dctByApprovalType[approvalTypeID];
                if (approvalSubtypeID in dctBySubtypeID) {
                    approvalRows = dctBySubtypeID[approvalSubtypeID];
                }
            }
        }

        //if there aren't any approval rows for the required fields, return undefined
        if (_.isUndefined(approvalRows) || _.isEmpty(approvalRows)) {
            return undefined;
        }

        return ApprovalService.findAndCacheApprovalWithOptions(approvalRows, options, optionsDctKey);

    }

    private static findAndCacheApprovalWithOptions(approvalRows: WB_ApprovalRow[], options: IApprovalServiceOptions, optionsDctKey: string) : WB_ApprovalRow {

        let validRules: WB_ApprovalRow[] = [];

        _.forEach(approvalRows, (aRow: WB_ApprovalRow) => {

           if (ApprovalService.isRuleValidWithOptions(aRow, options)) {
               validRules.push(aRow);
           }
        });

        if (_.isEmpty(validRules)) {
            return undefined;
        }

        let retVal: WB_ApprovalRow = _.first(validRules);

        if (!(retVal.cif.WB_BUSINESSUNIT_ID in ApprovalService.dctApprovalsByOptions)) {
            ApprovalService.dctApprovalsByOptions[retVal.cif.WB_BUSINESSUNIT_ID] = {};
        }
        let dctByApprovalType = ApprovalService.dctApprovalsByOptions[retVal.cif.WB_BUSINESSUNIT_ID];

        if (!(retVal.cif.APPROVAL_TYPE_ID in dctByApprovalType)) {
            dctByApprovalType[retVal.cif.APPROVAL_TYPE_ID] = {};
        }
        let dctBySubTypeId = dctByApprovalType[retVal.cif.APPROVAL_TYPE_ID];

        if (!(retVal.cif.APPROVAL_SUBTYPE_ID in dctBySubTypeId)) {
            dctBySubTypeId[retVal.cif.APPROVAL_SUBTYPE_ID] = {};
        }
        let dctByOptionsKey = dctBySubTypeId[retVal.cif.APPROVAL_SUBTYPE_ID];

        dctByOptionsKey[optionsDctKey] = retVal;

        return retVal;
    }

    private static isRuleValidWithOptions(aRow: WB_ApprovalRow, options: IApprovalServiceOptions): boolean {

        if (_.isUndefined(options)) {
            return true;
        }

        if (!DateUtils.RangesOverlap(options.effectiveDateRange, {StartDt: aRow.EffectiveStartDt, EndDt: aRow.EffectiveEndDt})) {
            return false;
        }

        if (!_.isUndefined(options.WB_CUST_ID)) {
            if (aRow.cif.WB_CUST_ID !=  0 && aRow.cif.WB_CUST_ID != options.WB_CUST_ID) {
                return false;
            }
        }

        if (!_.isUndefined(options.WB_EVTTYPE_ID)) {
            if (aRow.cif.WB_EVTTYPE_ID !=  0 && aRow.cif.WB_EVTTYPE_ID != options.WB_EVTTYPE_ID) {
                return false;
            }
        }

        if (!_.isUndefined(options.WB_EVTSTYPE_ID)) {
            if (aRow.cif.WB_EVTSTYPE_ID !=  0 && aRow.cif.WB_EVTSTYPE_ID != options.WB_EVTSTYPE_ID) {
                return false;
            }
        }

        if (!_.isUndefined(options.WB_PMTTYPE_ID)) {
            if (aRow.cif.WB_PMTTYPE_ID !=  0 && aRow.cif.WB_PMTTYPE_ID != options.WB_PMTTYPE_ID) {
                return false;
            }
        }

        if (!_.isUndefined(options.RATEBASIS) && !_.isEmpty(aRow.cif.RATEBASIS_OPTIONS)) {
            let dct = _.keyBy(aRow.cif.RATEBASIS_OPTIONS.split(","), (aVal) => {return aVal});
            if (_.isUndefined(dct[options.RATEBASIS])) {
                return false;
            }
        }

        if (!_.isUndefined(options.RATEUOM) && !_.isEmpty(aRow.cif.RATEUOM_OPTIONS)) {
            let dct = _.keyBy(aRow.cif.RATEUOM_OPTIONS.split(","), (aVal) => {return aVal});
            if (_.isUndefined(dct[options.RATEBASIS])) {
                return false;
            }
        }

        if (!_.isUndefined(options.PAYMENTMETHOD) && !_.isEmpty(aRow.cif.PAYMENTMETHOD_OPTIONS)) {
            let dct = _.keyBy(aRow.cif.PAYMENTMETHOD_OPTIONS.split(","), (aVal) => {return aVal});
            if (_.isUndefined(dct[options.PAYMENTMETHOD])) {
                return false;
            }
        }

        if (!_.isUndefined(options.RANGE_BOUND_TYPE)) {
            if (aRow.cif.RANGE_BOUND_TYPE !=  "" && aRow.cif.RANGE_BOUND_TYPE != options.RANGE_BOUND_TYPE) {
                return false;
            }
        }

        return true;
    }

    private static getOptionsDctKey(options?: IApprovalServiceOptions) : string {
        if (_.isUndefined(options)) {
            return "default";
        }
        let optionsArr: any[] = [];
        optionsArr.push(DateUtils.convertToSAPBW(options.effectiveDateRange.StartDt));
        optionsArr.push(DateUtils.convertToSAPBW(options.effectiveDateRange.EndDt));
        optionsArr.push(options.WB_CUST_ID);
        optionsArr.push(options.WB_EVTTYPE_ID);
        optionsArr.push(options.WB_EVTSTYPE_ID);
        optionsArr.push(options.WB_PMTTYPE_ID);
        optionsArr.push(options.RATEBASIS);
        optionsArr.push(options.RATEUOM);
        optionsArr.push(options.PAYMENTMETHOD);
        optionsArr.push(options.RANGE_BOUND_TYPE);
        return optionsArr.join("-");
    }

    //</editor-fold>

    //<editor-fold desc="Lease management">

    private static async reset() {
        ApprovalService.instantiateContext();
        let arr: any[] = _.values(ApprovalService.dctApprovalByWbID);
        await ApprovalService._dbContext.purgeCache(esWB_Approval, arr);
        ApprovalService.dctApprovalByWbID = {};
        ApprovalService.dctApprovalByWbID = {};
        ApprovalService._bIsLoaded = false;
        await ApprovalService.loadFromDB();
    }

    private static async loadFromDB(): Promise<void> {
        ApprovalService._bIsLoaded = false;
        ApprovalService.instantiateContext();
        let query: any = {where: {DELETED_IND: {neq: '1'}}};
        query.order = "EVALUATION_ORDER asc, EFFECTIVE_START_DT desc";

        let approvals: WB_ApprovalRow[] = await ApprovalService._dbContext.findAll(esWB_Approval, query);

        _.forEach(approvals, (anApprovalRow: WB_ApprovalRow) => {

            if (_.isUndefined(anApprovalRow.EffectiveStartDt)) {
                anApprovalRow.EffectiveStartDt = DateUtils.getMinDate();
            }
            if (_.isUndefined(anApprovalRow.EffectiveEndDt)) {
                anApprovalRow.EffectiveEndDt = DateUtils.getMaxDate();
            }

            if (!(anApprovalRow.cif.WB_BUSINESSUNIT_ID in ApprovalService.dctApprovals)) {
                ApprovalService.dctApprovals[anApprovalRow.cif.WB_BUSINESSUNIT_ID] = {};
            }
            let dctByApprovalType: any = ApprovalService.dctApprovals[anApprovalRow.cif.WB_BUSINESSUNIT_ID];
            if (!(anApprovalRow.cif.APPROVAL_TYPE_ID in dctByApprovalType)) {
                dctByApprovalType[anApprovalRow.cif.APPROVAL_TYPE_ID] = {};
            }
            let dctBySubType: any = dctByApprovalType[anApprovalRow.cif.APPROVAL_TYPE_ID];
            if (!(anApprovalRow.cif.APPROVAL_SUBTYPE_ID in dctBySubType)) {
                dctBySubType[anApprovalRow.cif.APPROVAL_SUBTYPE_ID] = [];
            }
            dctBySubType[anApprovalRow.cif.APPROVAL_SUBTYPE_ID].push(anApprovalRow);
            ApprovalService.dctApprovalByWbID[anApprovalRow.cif.WB_APPROVAL_ID] = anApprovalRow;
        });

        ApprovalService._bIsLoaded = true;
        ApprovalService._lease.resetLease();
    }

    private static instantiateContext() {
        if (_.isUndefined(ApprovalService._dbContext)) {
            ApprovalService._dbContext = new PAdbContext();
        }
    }

    private static async checkLease(): Promise<void> {
        if (_.isUndefined(ApprovalService._lease)) {
            ApprovalService._lease = new GenericLease(LeaseRegistry.Lease_Type_Approval);
        }

        if (!ApprovalService._bIsLoaded || await ApprovalService._lease.hasDataChanged()) {
            await ApprovalService.reset();
        }
    }

    //</editor-fold>

}
